import { useMemo, useState } from "react";

import { ChevronDown, ChevronRight } from "lucide-react";

import { TableSkeleton } from "~/components/ui/skeletons";
import {
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from "~/components/ui/table";
import { getWorkingDaysInfo } from "~/utils/workingDays";

import {
  type AnalysisViewMode,
  ClientGoalRow,
  MONTH_LABELS,
  computeYearlyAggregates,
} from "../types/salesGoals";
import ClientAnalysisTable from "./ClientAnalysisTable";
import ScrollableTableWrapper from "./ScrollableTableWrapper";

interface ChannelTabProps {
  clients: ClientGoalRow[];
  currentMonth: number;
  isLoading: boolean;
  viewMode: AnalysisViewMode;
  selectedMonth: number;
  year: number;
}

function formatCLP(amount: number): string {
  return "$" + Math.round(amount).toLocaleString("es-CL");
}

function formatPct(pct: number): string {
  return `${Math.round(pct)}%`;
}

function pctColor(pct: number): string {
  if (pct >= 80) return "text-emerald-600";
  if (pct >= 50) return "text-orange-500";
  return "text-red-500";
}

export default function ChannelTab({
  clients,
  currentMonth,
  isLoading,
  viewMode,
  selectedMonth,
  year,
}: ChannelTabProps) {
  if (viewMode === "monthly") {
    return (
      <ClientAnalysisTable
        clients={clients}
        selectedMonth={selectedMonth}
        year={year}
        groupBy="channel"
        isLoading={isLoading}
      />
    );
  }
  const grouped = useMemo(() => {
    const groups: Record<string, ClientGoalRow[]> = {};
    for (const c of clients) {
      const key = c.sales_channel_name || "Sin Canal";
      if (!groups[key]) groups[key] = [];
      groups[key].push(c);
    }
    return groups;
  }, [clients]);

  const [expandedChannels, setExpandedChannels] = useState<Set<string>>(
    new Set()
  );

  const toggleChannel = (channelName: string) => {
    setExpandedChannels((prev) => {
      const next = new Set(prev);
      if (next.has(channelName)) {
        next.delete(channelName);
      } else {
        next.add(channelName);
      }
      return next;
    });
  };

  if (isLoading) {
    return <TableSkeleton rows={8} columns={8} />;
  }

  const months = Array.from({ length: 12 }, (_, i) => i + 1);
  const currentMonthWd =
    currentMonth > 0 ? getWorkingDaysInfo(year, currentMonth) : undefined;

  let grandTotal = 0;
  let grandActualTotal = 0;
  const grandTotals: Record<number, number> = {};
  const grandActuals: Record<number, number> = {};
  for (const c of clients) {
    for (const m of months) {
      grandTotals[m] = (grandTotals[m] || 0) + (c.months[m] || 0);
      grandActuals[m] = (grandActuals[m] || 0) + (c.actuals[m] || 0);
    }
    grandTotal += c.total;
    grandActualTotal += c.totalActual;
  }

  return (
    <ScrollableTableWrapper>
      <TableHeader>
        <TableRow>
          <TableHead className="sticky left-0 z-10 min-w-[200px] whitespace-nowrap bg-card">
            Cliente
          </TableHead>
          <TableHead className="min-w-[110px] whitespace-nowrap">RUT</TableHead>
          <TableHead className="min-w-[130px] whitespace-nowrap">
            Vendedor
          </TableHead>
          {months.map((m) => (
            <TableHead
              key={m}
              className="min-w-[100px] whitespace-nowrap text-right"
            >
              {MONTH_LABELS[m]}
            </TableHead>
          ))}
          <TableHead className="min-w-[120px] whitespace-nowrap text-right font-bold">
            Total
          </TableHead>
          <TableHead className="min-w-[140px] whitespace-nowrap text-right font-bold">
            Deb. Llevar
          </TableHead>
          <TableHead className="min-w-[70px] whitespace-nowrap text-right font-bold">
            %
          </TableHead>
          <TableHead className="min-w-[140px] whitespace-nowrap text-right font-bold">
            Cierre Est.
          </TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {Object.entries(grouped).map(
          ([channelName, channelClients], groupIndex) => {
            const isExpanded = expandedChannels.has(channelName);
            const subtotals: Record<number, number> = {};
            const subtotalActuals: Record<number, number> = {};
            let subtotalTotal = 0;
            let subtotalActualTotal = 0;
            for (const c of channelClients) {
              for (const m of months) {
                subtotals[m] = (subtotals[m] || 0) + (c.months[m] || 0);
                subtotalActuals[m] =
                  (subtotalActuals[m] || 0) + (c.actuals[m] || 0);
              }
              subtotalTotal += c.total;
              subtotalActualTotal += c.totalActual;
            }

            const subAgg = computeYearlyAggregates(
              subtotals,
              subtotalActuals,
              year,
              currentMonth,
              currentMonthWd
            );

            return [
              <TableRow
                key={`header-${channelName}`}
                onClick={() => toggleChannel(channelName)}
                style={{
                  animationDelay: `${Math.min(groupIndex * 40, 400)}ms`,
                }}
                className="cursor-pointer duration-300 animate-in fade-in-0 slide-in-from-top-1 fill-mode-both hover:bg-muted/70"
              >
                <TableCell
                  colSpan={months.length + 7}
                  className="sticky left-0 z-10 bg-muted/50 text-sm font-bold"
                >
                  <div className="flex items-center gap-2">
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    {channelName} ({channelClients.length} clientes) - Meta:{" "}
                    {formatCLP(subtotalTotal)} | Real:{" "}
                    {formatCLP(subtotalActualTotal)} |{" "}
                    <span className={pctColor(subAgg.pctAchievement)}>
                      {formatPct(subAgg.pctAchievement)}
                    </span>
                  </div>
                </TableCell>
              </TableRow>,
              ...(isExpanded
                ? [
                    ...channelClients.map((client) => {
                      const agg = computeYearlyAggregates(
                        client.months,
                        client.actuals,
                        year,
                        currentMonth,
                        currentMonthWd
                      );
                      return (
                        <TableRow key={client.client_id}>
                          <TableCell className="sticky left-0 z-10 bg-card pl-6 font-medium">
                            {client.client_name}
                          </TableCell>
                          <TableCell className="text-xs text-muted-foreground">
                            {client.client_tax_id}
                          </TableCell>
                          <TableCell className="text-sm">
                            {client.seller_name}
                          </TableCell>
                          {months.map((m) => {
                            const goal = client.months[m] || 0;
                            const actual = client.actuals[m] || 0;
                            const showActual =
                              m <= currentMonth && (goal > 0 || actual > 0);
                            return (
                              <TableCell
                                key={m}
                                className="text-right tabular-nums"
                              >
                                <div>
                                  <span className="mr-1 text-xs text-muted-foreground">
                                    Meta:
                                  </span>
                                  {goal ? formatCLP(goal) : "-"}
                                </div>
                                {showActual && (
                                  <div
                                    className={`text-xs ${
                                      actual >= goal
                                        ? "text-emerald-600"
                                        : "text-orange-500"
                                    }`}
                                  >
                                    <span className="mr-1 text-muted-foreground">
                                      Real:
                                    </span>
                                    {formatCLP(actual)}
                                  </div>
                                )}
                              </TableCell>
                            );
                          })}
                          <TableCell className="text-right font-semibold tabular-nums">
                            <div>
                              <span className="mr-1 text-xs font-normal text-muted-foreground">
                                Meta:
                              </span>
                              {formatCLP(client.total)}
                            </div>
                            {currentMonth > 0 && (
                              <div
                                className={`text-xs ${
                                  client.totalActual >= client.total
                                    ? "text-emerald-600"
                                    : "text-orange-500"
                                }`}
                              >
                                <span className="mr-1 font-normal text-muted-foreground">
                                  Real:
                                </span>
                                {formatCLP(client.totalActual)}
                              </div>
                            )}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {currentMonth > 0
                              ? formatCLP(agg.deberiaLlevar)
                              : "-"}
                          </TableCell>
                          <TableCell
                            className={`text-right font-semibold tabular-nums ${currentMonth > 0 ? pctColor(agg.pctAchievement) : ""}`}
                          >
                            {currentMonth > 0
                              ? formatPct(agg.pctAchievement)
                              : "-"}
                          </TableCell>
                          <TableCell className="text-right tabular-nums">
                            {formatCLP(agg.cierreEstimado)}
                          </TableCell>
                        </TableRow>
                      );
                    }),
                    <TableRow
                      key={`subtotal-${channelName}`}
                      className="border-b-2"
                    >
                      <TableCell className="sticky left-0 z-10 bg-muted/30 pl-6 text-xs font-semibold uppercase text-muted-foreground">
                        Subtotal {channelName}
                      </TableCell>
                      <TableCell className="bg-muted/30" />
                      <TableCell className="bg-muted/30" />
                      {months.map((m) => {
                        const goal = subtotals[m] || 0;
                        const actual = subtotalActuals[m] || 0;
                        const showActual =
                          m <= currentMonth && (goal > 0 || actual > 0);
                        return (
                          <TableCell
                            key={m}
                            className="bg-muted/30 text-right font-semibold tabular-nums"
                          >
                            <div>
                              <span className="mr-1 text-xs font-normal text-muted-foreground">
                                Meta:
                              </span>
                              {goal ? formatCLP(goal) : "-"}
                            </div>
                            {showActual && (
                              <div
                                className={`text-xs font-medium ${
                                  actual >= goal
                                    ? "text-emerald-600"
                                    : "text-orange-500"
                                }`}
                              >
                                <span className="mr-1 font-normal text-muted-foreground">
                                  Real:
                                </span>
                                {formatCLP(actual)}
                              </div>
                            )}
                          </TableCell>
                        );
                      })}
                      <TableCell className="bg-muted/30 text-right font-bold tabular-nums">
                        <div>
                          <span className="mr-1 text-xs font-normal text-muted-foreground">
                            Meta:
                          </span>
                          {formatCLP(subtotalTotal)}
                        </div>
                        {currentMonth > 0 && (
                          <div
                            className={`text-xs font-medium ${
                              subtotalActualTotal >= subtotalTotal
                                ? "text-emerald-600"
                                : "text-orange-500"
                            }`}
                          >
                            <span className="mr-1 font-normal text-muted-foreground">
                              Real:
                            </span>
                            {formatCLP(subtotalActualTotal)}
                          </div>
                        )}
                      </TableCell>
                      <TableCell className="bg-muted/30 text-right font-bold tabular-nums">
                        {currentMonth > 0
                          ? formatCLP(subAgg.deberiaLlevar)
                          : "-"}
                      </TableCell>
                      <TableCell
                        className={`bg-muted/30 text-right font-bold tabular-nums ${currentMonth > 0 ? pctColor(subAgg.pctAchievement) : ""}`}
                      >
                        {currentMonth > 0
                          ? formatPct(subAgg.pctAchievement)
                          : "-"}
                      </TableCell>
                      <TableCell className="bg-muted/30 text-right font-bold tabular-nums">
                        {formatCLP(subAgg.cierreEstimado)}
                      </TableCell>
                    </TableRow>,
                  ]
                : []),
            ];
          }
        )}
      </TableBody>
      <TableFooter>
        <TableRow>
          <TableCell className="sticky left-0 z-10 font-bold">
            Total General
          </TableCell>
          <TableCell />
          <TableCell />
          {months.map((m) => {
            const goal = grandTotals[m] || 0;
            const actual = grandActuals[m] || 0;
            const showActual = m <= currentMonth && (goal > 0 || actual > 0);
            return (
              <TableCell key={m} className="text-right font-bold tabular-nums">
                <div>
                  <span className="mr-1 text-xs font-normal text-muted-foreground">
                    Meta:
                  </span>
                  {goal ? formatCLP(goal) : "-"}
                </div>
                {showActual && (
                  <div
                    className={`text-xs font-medium ${
                      actual >= goal ? "text-emerald-600" : "text-orange-500"
                    }`}
                  >
                    <span className="mr-1 font-normal text-muted-foreground">
                      Real:
                    </span>
                    {formatCLP(actual)}
                  </div>
                )}
              </TableCell>
            );
          })}
          <TableCell className="text-right font-bold tabular-nums">
            <div>
              <span className="mr-1 text-xs font-normal text-muted-foreground">
                Meta:
              </span>
              {formatCLP(grandTotal)}
            </div>
            {currentMonth > 0 && (
              <div
                className={`text-xs font-medium ${
                  grandActualTotal >= grandTotal
                    ? "text-emerald-600"
                    : "text-orange-500"
                }`}
              >
                <span className="mr-1 font-normal text-muted-foreground">
                  Real:
                </span>
                {formatCLP(grandActualTotal)}
              </div>
            )}
          </TableCell>
          {(() => {
            const grandAgg = computeYearlyAggregates(
              grandTotals,
              grandActuals,
              year,
              currentMonth,
              currentMonthWd
            );
            return (
              <>
                <TableCell className="text-right font-bold tabular-nums">
                  {currentMonth > 0 ? formatCLP(grandAgg.deberiaLlevar) : "-"}
                </TableCell>
                <TableCell
                  className={`text-right font-bold tabular-nums ${currentMonth > 0 ? pctColor(grandAgg.pctAchievement) : ""}`}
                >
                  {currentMonth > 0 ? formatPct(grandAgg.pctAchievement) : "-"}
                </TableCell>
                <TableCell className="text-right font-bold tabular-nums">
                  {formatCLP(grandAgg.cierreEstimado)}
                </TableCell>
              </>
            );
          })()}
        </TableRow>
      </TableFooter>
    </ScrollableTableWrapper>
  );
}
